import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  exportAllRequestsExcel,
  fetchHrAssignedBreakdown,
  fetchHrRequests,
  updateRequestStatus,
} from '../../services/allRequestsApi';
import api from '../../services/api';
import { formatRoleLabel, parseStoredList } from '../../utils/roleCapabilities';
import { getColumnsForTab } from '../../utils/columnConfig';
import { DataTableShell, PageHeader, PageLayout, StatusBadge } from '../../components/ui';
import RequestsFiltersPanel from './components/RequestsFiltersPanel';
import RequestsDesktopTable from './components/RequestsDesktopTable';
import RequestsMobileCards from './components/RequestsMobileCards';
import {
  canAuthorPreApprovalRequest,
  formatDate,
  getLocalEmployeeCode,
  getLocalRole,
  isHrAdminRole,
  lastNDays,
  normalizeDisplayValue,
  REQUEST_TYPE_TABS,
} from './components/requestsUiUtils';

function useDebouncedValue(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const HOD_ROLE_KEYS = ['hod', 'head_of_department', 'head_department', 'head_of_dept'];
const OPERATIONS_ROLE_KEYS = ['operation_manager', 'operations_manager', 'operations', 'operation'];
const EXECUTIVE_ROLE_KEYS = [
  'executive',
  'executive_manager',
  'executive_director',
  'executive_managing_director',
];

const createReviewDialogState = () => ({
  open: false,
  request: null,
  action: 'approved',
  reason: '',
  revision_target_step: '',
  pre_approval_billing_mode: '',
  client_billing: '',
  marginal_benefit: '',
  missing_punch_effective_time: '',
  missing_punch_effective_project_id: '',
  missing_punch_hr_note: '',
});

const PRE_APPROVAL_BILLING_OPTIONS = [
  { value: 'billed', label: 'Billed' },
  { value: 'lumpsum', label: 'Lumpsum' },
  { value: 'billing_hq', label: 'Billing HQ' },
];

const isHodRole = (role) => HOD_ROLE_KEYS.includes(String(role || '').toLowerCase());
const isOperationsRole = (role) => OPERATIONS_ROLE_KEYS.includes(String(role || '').toLowerCase());
const isExecutiveRole = (role) => EXECUTIVE_ROLE_KEYS.includes(String(role || '').toLowerCase());
const normalizePreApprovalBillingMode = (value) => String(value || '').trim().toLowerCase();
const isBillingHqMode = (value) => normalizePreApprovalBillingMode(value) === 'billing_hq';
const normalizeMainTabFromQuery = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['assigned', 'assigned_requests', 'queue'].includes(normalized)) return 'assigned_requests';
  if (['archive', 'archive_requests', 'history'].includes(normalized)) return 'archive_requests';
  if (['revisions', 'pre_approval_revisions', 'revision_inbox'].includes(normalized)) {
    return 'pre_approval_revisions';
  }
  return 'my_requests';
};
const normalizeRequestTypeFromQuery = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized || 'all';
};
// Workflow step keys are mostly role names (manager / hod / ceo / hr_admin),
// so route them through formatRoleLabel for canonical capitalization.
const formatWorkflowStepLabel = (value = '') => formatRoleLabel(value);
const normalizeId = (value) => String(value ?? '').trim();
const hasStoredPermission = (permissions, key) => {
  const normalized = permissions.map((permission) => String(permission || '').trim().toLowerCase());
  return normalized.includes('*') || normalized.includes(key);
};

const filenameFromDisposition = (disposition, fallback) => {
  const value = String(disposition || '');
  const encodedMatch = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) return decodeURIComponent(encodedMatch[1].replace(/"/g, ''));

  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || fallback;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const toTimeInputValue = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return '';
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};
const getMissingPunchProject = (request, effective = false) =>
  effective
    ? request?.missing_punch_effective_project || request?.missingPunchEffectiveProject || null
    : request?.missing_punch_project || request?.missingPunchProject || null;
const getProjectLabel = (project) => {
  if (!project) return '';
  const code = project.code || project.project_code || project.projectCode;
  return [project.name, code ? `(${code})` : null].filter(Boolean).join(' ');
};
const normalizeRequestTypeToken = (value = '') =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
const requestMatchesType = (request, expectedType, phrase = '') => {
  const expected = normalizeRequestTypeToken(expectedType);
  const normalizedPhrase = String(phrase || expectedType || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, ' ');

  return [
    request?.request_type,
    request?.type,
    request?.type_key,
    request?.type_label,
    request?.type_detail,
    request?.reason_preview,
  ]
    .filter(Boolean)
    .some((value) => {
      const raw = String(value || '').trim().toLowerCase();
      const token = normalizeRequestTypeToken(raw);
      return token === expected || token.includes(expected) || (normalizedPhrase && raw.includes(normalizedPhrase));
    });
};
const isPreApprovalRequest = (request) => requestMatchesType(request, 'pre_approval', 'pre approval');
const isMissingPunchRequest = (request) =>
  requestMatchesType(request, 'missing_punch', 'missing punch') ||
  requestMatchesType(request, 'missing_punch', 'missed punch');
const getWorkflowTaskMetadata = (request) =>
  Array.isArray(request?.workflow_task_metadata) ? request.workflow_task_metadata : [];
// Mirrors the backend isUserActivePreApprovalRevisionTarget for list rows: the
// person a parked revision is assigned to. They may reject the request outright
// (with a reason) while it waits for revision — approvals stay blocked, so the
// row shows a lone Reject action. hr_team_creator revisions live in the
// creator's own tabs, not the assigned queue; the detail page handles those.
const isPreApprovalRevisionAssignee = (request, role, employeeCode) => {
  if (String(request?.pre_approval_status || '').toLowerCase() !== 'revision_requested') return false;

  const target = String(request?.pre_approval_revision_target_step || '').toLowerCase();
  const code = String(employeeCode || '').trim();

  if (target === 'hr_admin') return isHrAdminRole(role);
  if (!code) return false;
  if (target === 'hod') return String(request?.pre_approval_hod_code || '') === code;
  if (target === 'operations_manager' || target === 'operation_manager') {
    return String(request?.pre_approval_operations_code || '') === code;
  }
  if (target === 'ceo') return String(request?.pre_approval_ceo_code || '') === code;
  if (target === 'executive_managing_director' || target === 'executive') {
    return String(request?.pre_approval_executive_md_code || '') === code;
  }
  return false;
};
const hasWorkflowEngineInstance = (request) =>
  Boolean(request?.workflow_instance || request?.workflowInstance || getWorkflowTaskMetadata(request).length);
const getPendingWorkflowTask = (request) =>
  getWorkflowTaskMetadata(request).find(
    (task) => String(task?.status || '').toLowerCase() === 'pending' && task?.task_id
  ) || null;
const getWorkflowActionPayload = (request) => {
  const task = getPendingWorkflowTask(request);
  return task?.task_id ? { workflow_task_id: task.task_id } : {};
};

function RequestQueueFocusPanel({
  roleLabel,
  isAssignedView,
  isArchiveView,
  requestType,
  visibleCount,
  assignedTotal,
  archivedTotal,
  actionableCount,
  selectedCount,
  loading = false,
}) {
  const theme = useTheme();
  const modeLabel = isArchiveView
    ? 'Reviewed archive'
    : isAssignedView
      ? 'Assigned action queue'
      : 'My request activity';
  const modeCaption = isArchiveView
    ? 'Previously reviewed requests in your visible role scope.'
    : isAssignedView
      ? 'Requests currently routed to your role for action.'
      : 'Requests you created in the selected date range.';
  const typeLabel = requestType === 'all' ? 'All types' : formatWorkflowStepLabel(requestType);
  // Render an em-dash placeholder while the request totals are still
  // loading so the page doesn't flash "0 / 0 / 0" before data arrives.
  const formatCount = (value) => (loading ? '—' : value);
  const cards = [
    {
      label: 'Visible',
      value: formatCount(visibleCount),
      helper: `${typeLabel} in the current view`,
      color: theme.palette.primary.main,
    },
    {
      label: 'Pending',
      value: formatCount(assignedTotal),
      helper: 'Live assigned workload',
      color: theme.palette.warning.main,
    },
    {
      label: 'Actionable',
      value: formatCount(actionableCount),
      helper: isAssignedView ? 'Rows you can decide now' : 'Only shown in assigned mode',
      color: theme.palette.success.main,
    },
    {
      label: 'Archive',
      value: formatCount(archivedTotal),
      helper: 'Reviewed requests kept for traceability',
      color: theme.palette.info.main,
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.25,
        p: { xs: 1.1, md: 1.25 },
        borderRadius: '8px',
        borderColor: alpha(theme.palette.primary.main, 0.16),
        bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.62 : 0.92),
        backgroundImage:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, transparent 58%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.065)}, transparent 62%)`,
        boxShadow: theme.hr?.shadow?.soft || theme.shadows[2],
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 0.75fr) minmax(0, 1.25fr)' },
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        <Box>
          <Stack spacing={0.75} sx={{ height: '100%', justifyContent: 'center' }}>
            <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap', rowGap: 0.7 }}>
              <Chip size="small" label={roleLabel} color="primary" variant="outlined" sx={{ borderRadius: '8px' }} />
              <Chip size="small" label={modeLabel} variant="outlined" sx={{ borderRadius: '8px' }} />
              {selectedCount ? <Chip size="small" label={`${selectedCount} selected`} color="success" /> : null}
            </Stack>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {modeLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
              {modeCaption}
            </Typography>
          </Stack>
        </Box>

        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 0.8,
            }}
          >
            {cards.map((card) => (
              <Box
                key={card.label}
                sx={{
                  height: '100%',
                  minHeight: 82,
                  p: 1,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: alpha(card.color, 0.18),
                  bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.5 : 0.78),
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
                  {card.label}
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.15, fontWeight: 900, lineHeight: 1 }}>
                  {card.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

function RequestsWithAssignedSplit() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const initialQuery = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialMainTab = useMemo(
    () => normalizeMainTabFromQuery(initialQuery.get('view') || initialQuery.get('tab')),
    [initialQuery]
  );
  const initialRequestType = useMemo(
    () => normalizeRequestTypeFromQuery(initialQuery.get('request_type') || initialQuery.get('type')),
    [initialQuery]
  );

  const [mainTab, setMainTab] = useState(initialMainTab);
  const [requestType, setRequestType] = useState(initialRequestType);

  const initialRange = useMemo(() => lastNDays(90), []); // default: last 3 months
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);
  const searchRef = useRef(null);

  const [requests, setRequests] = useState([]);
  // Start in loading state so the first paint isn't a flash of "0 pending /
  // 0 archive / queue is clear" before the breakdown call resolves.
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const [assignedPendingBreakdown, setAssignedPendingBreakdown] = useState({});
  const [assignedArchiveBreakdown, setAssignedArchiveBreakdown] = useState({});

  const [selectedIds, setSelectedIds] = useState([]);
  const [reviewDialog, setReviewDialog] = useState(createReviewDialogState);
  // Lightweight confirm prompt for simple (non pre-approval / non missing-punch)
  // assigned-queue actions so an accidental Approve click can't fire a PATCH
  // straight through. Pre-approval and missing-punch keep their richer review
  // dialog which already requires explicit Approve/Reject/Send Revision.
  const [confirmDialog, setConfirmDialog] = useState({ open: false, request: null, action: null });
  const [missingPunchProjects, setMissingPunchProjects] = useState([]);
  const [missingPunchProjectsLoading, setMissingPunchProjectsLoading] = useState(false);
  const reviewBillingMode = normalizePreApprovalBillingMode(reviewDialog.pre_approval_billing_mode);
  const reviewIsBillingHq = reviewBillingMode === 'billing_hq';

  const role = getLocalRole();
  const permissions = useMemo(() => parseStoredList('permissions'), []);
  const isPlatformAdmin = localStorage.getItem('is_platform_admin') === '1';
  const isHrAdmin = isHrAdminRole(role);
  const canExportAllRequestsExcel =
    isPlatformAdmin || isHrAdmin || hasStoredPermission(permissions, 'requests.export_all');
  const myEmployeeCode = getLocalEmployeeCode();
  // Only HR Admin (the canonical HR-side approver) gets the Assigned /
  // Archive tabs. Other HR-team roles (hr_team, hr_manager, hr_officer,
  // hr_generalist, recruiter, etc.) act like employees here — they create
  // their own requests, get the Pre-Approval catalog and Revisions tab,
  // but they don't review the cross-tenant approval queue. Approver-family
  // roles (manager, hod, ops, ceo, executive, finance, finance_coordinator)
  // keep their existing access.
  const canViewAssignedTab =
    isHrAdmin ||
    [
      'manager',
      'finance',
      'ceo',
      'finance_coordinator',
      'operation_manager',
      'operations',
      'executive',
      'executive_manager',
      'executive_director',
      'executive_managing_director',
      'hod',
      'head_of_department',
      'head_department',
      'head_of_dept',
    ].includes(role);

  const reviewRequest = reviewDialog.request;
  const reviewIsPreApproval = isPreApprovalRequest(reviewRequest);
  const reviewIsMissingPunch = isMissingPunchRequest(reviewRequest);
  const reviewCurrentStep = String(reviewRequest?.pre_approval_current_step || '').toLowerCase();
  const reviewIsHodApproval = reviewDialog.action === 'approved' && reviewCurrentStep === 'hod';
  const submittedMissingPunchProject = getMissingPunchProject(reviewRequest);
  const effectiveMissingPunchProject = getMissingPunchProject(reviewRequest, true);
  const submittedMissingPunchProjectId = normalizeId(
    reviewRequest?.missing_punch_project_id ?? submittedMissingPunchProject?.id
  );
  const submittedMissingPunchTime = toTimeInputValue(reviewRequest?.missing_punch_time);
  const effectiveMissingPunchTime = toTimeInputValue(
    reviewDialog.missing_punch_effective_time || reviewRequest?.missing_punch_effective_time || reviewRequest?.missing_punch_time
  );
  const effectiveMissingPunchProjectId = normalizeId(
    reviewDialog.missing_punch_effective_project_id ||
      reviewRequest?.missing_punch_effective_project_id ||
      reviewRequest?.missing_punch_project_id ||
      submittedMissingPunchProject?.id
  );
  const missingPunchTimeChanged =
    reviewIsMissingPunch && effectiveMissingPunchTime && submittedMissingPunchTime
      ? effectiveMissingPunchTime !== submittedMissingPunchTime
      : false;
  const missingPunchProjectChanged =
    reviewIsMissingPunch && effectiveMissingPunchProjectId !== submittedMissingPunchProjectId;
  const missingPunchHrNoteRequired =
    reviewIsMissingPunch &&
    reviewDialog.action === 'approved' &&
    (missingPunchTimeChanged || missingPunchProjectChanged);
  const missingPunchProjectOptions = useMemo(() => {
    const options = Array.isArray(missingPunchProjects) ? [...missingPunchProjects] : [];
    [submittedMissingPunchProject, effectiveMissingPunchProject].forEach((project) => {
      if (!project?.id) return;
      if (!options.some((option) => normalizeId(option.id) === normalizeId(project.id))) {
        options.unshift(project);
      }
    });
    return options;
  }, [effectiveMissingPunchProject, missingPunchProjects, submittedMissingPunchProject]);
  const selectedMissingPunchProject = useMemo(
    () =>
      missingPunchProjectOptions.find(
        (project) => normalizeId(project.id) === effectiveMissingPunchProjectId
      ) || null,
    [effectiveMissingPunchProjectId, missingPunchProjectOptions]
  );
  const reviewTargetOptions = useMemo(() => {
    if (!reviewIsPreApproval) return [];
    if (['operations_manager', 'ceo', 'executive_managing_director'].includes(reviewCurrentStep)) {
      return [
        { value: 'hr_team_creator', label: 'HR Team Creator' },
        { value: 'hod', label: 'Head of Department' },
      ];
    }

    return [{ value: 'hr_team_creator', label: 'HR Team Creator' }];
  }, [reviewCurrentStep, reviewIsPreApproval]);

  const reviewComputedMargin = useMemo(() => {
    if (reviewDialog.pre_approval_billing_mode !== 'billed') {
      return reviewDialog.marginal_benefit;
    }

    const totalCost = Number(reviewRequest?.total_cost_to_company || 0);
    const clientBilling = Number(reviewDialog.client_billing || 0);
    if (!Number.isFinite(totalCost) || !Number.isFinite(clientBilling) || clientBilling <= 0) {
      return '';
    }

    return ((1 - totalCost / clientBilling) * 100).toFixed(2);
  }, [
    reviewDialog.client_billing,
    reviewDialog.marginal_benefit,
    reviewDialog.pre_approval_billing_mode,
    reviewRequest?.total_cost_to_company,
  ]);

  useEffect(() => {
    if (!reviewDialog.open || !reviewIsMissingPunch) return undefined;

    let mounted = true;
    setMissingPunchProjectsLoading(true);
    api.get('/projects')
      .then((res) => {
        if (!mounted) return;
        setMissingPunchProjects(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to fetch projects for missing punch action', err);
        if (mounted) setMissingPunchProjects([]);
      })
      .finally(() => {
        if (mounted) setMissingPunchProjectsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [reviewDialog.open, reviewIsMissingPunch]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const nextMainTab = normalizeMainTabFromQuery(query.get('view') || query.get('tab'));
    const nextRequestType = normalizeRequestTypeFromQuery(query.get('request_type') || query.get('type'));

    setMainTab(canViewAssignedTab ? nextMainTab : 'my_requests');
    setRequestType(nextRequestType);
    setSelectedIds([]);
  }, [canViewAssignedTab, location.search]);

  useEffect(() => {
    if (!canViewAssignedTab && mainTab !== 'my_requests') {
      setMainTab('my_requests');
    }
  }, [canViewAssignedTab, mainTab]);

  const isAssignedView = mainTab === 'assigned_requests';
  const isArchiveView = mainTab === 'archive_requests';
  const isRevisionsView = mainTab === 'pre_approval_revisions';
  const isPreApprovalQueueView = isAssignedView && normalizeRequestTypeToken(requestType) === 'pre_approval';
  const queue = isArchiveView ? 'archive' : 'pending';

  const assignedTotal = assignedPendingBreakdown.total || 0;
  const archivedTotal = assignedArchiveBreakdown.total || 0;
  const activeBreakdown = isArchiveView ? assignedArchiveBreakdown : assignedPendingBreakdown;

  // The Revisions tab is for pre-approval creators (HR team + the
  // management roles allowed to author pre-approvals). It surfaces their
  // own pre-approval requests that are sitting in `revision_requested`
  // state with `pre_approval_revision_target_step === 'hr_team_creator'`,
  // so the requester can edit and resubmit without hunting through the
  // generic "My Requests" list.
  const canSeePreApprovalRevisions = canAuthorPreApprovalRequest(role);
  const isRevisionTargetingCreator = useCallback((req) => (
    req?.request_type === 'pre_approval'
    && String(req?.pre_approval_status || '').toLowerCase() === 'revision_requested'
    && String(req?.pre_approval_revision_target_step || '').toLowerCase() === 'hr_team_creator'
  ), []);
  const revisionsTotal = useMemo(
    () => (Array.isArray(requests) ? requests : []).filter(isRevisionTargetingCreator).length,
    [requests, isRevisionTargetingCreator],
  );

  const mainTabs = canViewAssignedTab
    ? [
        { key: 'my_requests', label: 'My Requests' },
        { key: 'assigned_requests', label: 'Assigned', count: assignedTotal, attention: assignedTotal > 0 },
        ...(canSeePreApprovalRevisions
          ? [{
              key: 'pre_approval_revisions',
              label: 'Revisions',
              count: revisionsTotal,
              attention: revisionsTotal > 0,
            }]
          : []),
        { key: 'archive_requests', label: 'Archive', count: archivedTotal },
      ]
    : [
        { key: 'my_requests', label: 'My Requests' },
        ...(canSeePreApprovalRevisions
          ? [{
              key: 'pre_approval_revisions',
              label: 'Revisions',
              count: revisionsTotal,
              attention: revisionsTotal > 0,
            }]
          : []),
      ];

  const buildRequestsListPath = useCallback((overrides = {}) => {
    const nextMainTab = overrides.mainTab ?? mainTab;
    const nextRequestType = overrides.requestType ?? requestType;
    const params = new URLSearchParams(location.search);

    if (nextMainTab && nextMainTab !== 'my_requests') {
      params.set('view', nextMainTab);
    } else {
      params.delete('view');
    }

    if (nextRequestType && nextRequestType !== 'all') {
      params.set('request_type', nextRequestType);
    } else {
      params.delete('request_type');
    }

    const query = params.toString();
    return `/all-requests${query ? `?${query}` : ''}`;
  }, [location.search, mainTab, requestType]);

  const buildRequestDetailPath = useCallback((requestId) => {
    const params = new URLSearchParams();
    params.set('from', mainTab || 'my_requests');
    if (requestType && requestType !== 'all') {
      params.set('request_type', requestType);
    }

    return `/hr-requests/${requestId}?${params.toString()}`;
  }, [mainTab, requestType]);

  const openRequestDetails = useCallback((requestId) => {
    if (!requestId) return;
    // From the Revisions tab, jump straight into the editable form so the
    // requester can fix and resubmit in one step instead of bouncing
    // through the read-only detail page first.
    if (mainTab === 'pre_approval_revisions') {
      navigate(`/hr-requests/${requestId}/edit`, {
        state: { returnTo: buildRequestsListPath() },
      });
      return;
    }
    navigate(buildRequestDetailPath(requestId), {
      state: { returnTo: buildRequestsListPath() },
    });
  }, [buildRequestDetailPath, buildRequestsListPath, mainTab, navigate]);

  const handleMainTabChange = (e, newValue) => {
    setMainTab(newValue);
    setSelectedIds([]);
    navigate(buildRequestsListPath({ mainTab: newValue }), { replace: false });
  };

  const finalSubTabs = useMemo(() => {
    let tabs = REQUEST_TYPE_TABS;

    if (role === 'manager' && mainTab !== 'my_requests') {
      tabs = tabs.filter((st) => !['loans', 'finance_claim', 'bank'].includes(st.key));
    }
    if (
      ['hod', 'head_of_department', 'head_department', 'head_of_dept'].includes(role) &&
      mainTab !== 'my_requests'
    ) {
      tabs = tabs.filter((st) => ['pre_approval'].includes(st.key));
    }
    if (role === 'finance_coordinator' && mainTab !== 'my_requests') {
      tabs = tabs.filter((st) => ['finance_claim'].includes(st.key));
    }
    if (['operation_manager', 'operations'].includes(role) && mainTab !== 'my_requests') {
      tabs = tabs.filter((st) => ['pre_approval'].includes(st.key));
    }
    if (
      EXECUTIVE_ROLE_KEYS.includes(role) &&
      mainTab !== 'my_requests'
    ) {
      tabs = tabs.filter((st) => ['pre_approval'].includes(st.key));
    }
    if (role === 'ceo' && mainTab !== 'my_requests') {
      tabs = tabs.filter((st) => ['loans', 'pre_approval'].includes(st.key));
    }

    return tabs;
  }, [role, mainTab]);

  const handleRequestTypeChange = (e) => {
    const nextRequestType = e.target.value;
    setRequestType(nextRequestType);
    setSelectedIds([]);
    navigate(buildRequestsListPath({ requestType: nextRequestType }), { replace: false });
  };

  useEffect(() => {
    const allowed = new Set(['all', ...finalSubTabs.map((t) => t.key)]);
    if (!allowed.has(requestType)) {
      setRequestType('all');
      setSelectedIds([]);
    }
  }, [finalSubTabs, requestType]);

  const refreshBreakdowns = useCallback(async () => {
    if (!canViewAssignedTab) {
      setAssignedPendingBreakdown({});
      setAssignedArchiveBreakdown({});
      return;
    }

    try {
      const [pendingRes, archiveRes] = await Promise.all([
        fetchHrAssignedBreakdown('pending'),
        fetchHrAssignedBreakdown('archive'),
      ]);
      setAssignedPendingBreakdown(pendingRes || {});
      setAssignedArchiveBreakdown(archiveRes || {});
    } catch (err) {
      console.error('Failed to fetch assigned/archive breakdown:', err);
    }
  }, [canViewAssignedTab]);

  useEffect(() => {
    refreshBreakdowns();
  }, [refreshBreakdowns]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const workQueueMode = mainTab === 'assigned_requests' || mainTab === 'archive_requests';
      const revisionsMode = mainTab === 'pre_approval_revisions';
      const payload = {
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        view_mode: workQueueMode ? 'assigned_requests' : 'my_requests',
      };

      if (workQueueMode) payload.queue = queue;
      // The Revisions tab is always scoped to pre-approval; client-side
      // filter then narrows to revision_requested + hr_team_creator.
      if (revisionsMode) {
        payload.request_type = 'pre_approval';
      } else if (requestType && requestType !== 'all') {
        payload.request_type = requestType;
      }

      const data = await fetchHrRequests(payload);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch requests:', err);
      setError('Failed to fetch requests. Please try again.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, mainTab, queue, requestType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const closeReviewDialog = useCallback(() => {
    setReviewDialog(createReviewDialogState());
  }, []);

  const openPreApprovalReviewDialog = useCallback((request, action) => {
    const submittedProject = getMissingPunchProject(request);
    const effectiveProject = getMissingPunchProject(request, true);
    setReviewDialog({
      open: true,
      request,
      action,
      reason: '',
      revision_target_step: action === 'revised' ? 'hr_team_creator' : '',
      pre_approval_billing_mode: String(request?.pre_approval_billing_mode || '').trim(),
      client_billing: request?.client_billing != null ? String(request.client_billing) : '',
      marginal_benefit: request?.marginal_benefit != null ? String(request.marginal_benefit) : '',
      missing_punch_effective_time: toTimeInputValue(
        request?.missing_punch_effective_time || request?.missing_punch_time
      ),
      missing_punch_effective_project_id: normalizeId(
        request?.missing_punch_effective_project_id ||
          effectiveProject?.id ||
          request?.missing_punch_project_id ||
          submittedProject?.id
      ),
      missing_punch_hr_note: String(request?.missing_punch_hr_note || ''),
    });
  }, []);

  const submitPreApprovalReview = useCallback(async () => {
    if (!reviewRequest?.id) return;

    const payload = {};
    const trimmedReason = String(reviewDialog.reason || '').trim();
    Object.assign(payload, getWorkflowActionPayload(reviewRequest));

    if (reviewDialog.action === 'rejected') {
      if (!trimmedReason) {
        setError('Rejection reason is required.');
        return;
      }
      payload.rejection_reason = trimmedReason;
    }

    if (reviewIsMissingPunch) {
      const hrNote = String(reviewDialog.missing_punch_hr_note || '').trim();

      if (reviewDialog.action === 'approved') {
        const effectiveTime = effectiveMissingPunchTime || submittedMissingPunchTime;
        if (!effectiveTime) {
          setError('Effective punch time is required before approval.');
          return;
        }
        if (missingPunchHrNoteRequired && !hrNote) {
          setError('HR note is required when changing the submitted punch time or project.');
          return;
        }

        payload.missing_punch_effective_time = effectiveTime;
        if (effectiveMissingPunchProjectId) {
          payload.missing_punch_effective_project_id = effectiveMissingPunchProjectId;
        }
      }

      if (hrNote) {
        payload.missing_punch_hr_note = hrNote;
      }
    }

    if (reviewDialog.action === 'revised') {
      if (!reviewDialog.revision_target_step) {
        setError('Revision target is required.');
        return;
      }
      if (!trimmedReason) {
        setError('Revision reason is required.');
        return;
      }
      payload.revision_target_step = reviewDialog.revision_target_step;
      payload.revision_reason = trimmedReason;
    }

    if (reviewIsHodApproval) {
      const billingMode = String(reviewDialog.pre_approval_billing_mode || '').trim();
      const clientBilling = Number(reviewDialog.client_billing || 0);
      const manualMargin = String(reviewDialog.marginal_benefit || '').trim();

      if (!billingMode) {
        setError('Billing mode is required for HOD approval.');
        return;
      }
      if (!isBillingHqMode(billingMode) && (!Number.isFinite(clientBilling) || clientBilling <= 0)) {
        setError('Billing value must be greater than zero.');
        return;
      }
      if (billingMode === 'lumpsum' && !manualMargin) {
        setError('Margin benefit is required for lumpsum billing.');
        return;
      }

      payload.pre_approval_billing_mode = billingMode;
      payload.client_billing = isBillingHqMode(billingMode) ? null : reviewDialog.client_billing;
      payload.marginal_benefit = isBillingHqMode(billingMode)
        ? null
        : billingMode === 'billed'
          ? reviewComputedMargin
          : manualMargin;
    }

    setUpdating(true);
    setError(null);
    try {
      await updateRequestStatus(reviewRequest.id, reviewDialog.action, payload);
      closeReviewDialog();
      await fetchData();
      if (mainTab !== 'my_requests') await refreshBreakdowns();
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to update request status.');
    } finally {
      setUpdating(false);
    }
  }, [
    closeReviewDialog,
    fetchData,
    mainTab,
    refreshBreakdowns,
    reviewComputedMargin,
    reviewDialog.action,
    reviewDialog.client_billing,
    reviewDialog.marginal_benefit,
    reviewDialog.missing_punch_hr_note,
    reviewDialog.pre_approval_billing_mode,
    reviewDialog.reason,
    reviewDialog.revision_target_step,
    reviewIsMissingPunch,
    reviewIsHodApproval,
    missingPunchHrNoteRequired,
    effectiveMissingPunchProjectId,
    effectiveMissingPunchTime,
    submittedMissingPunchTime,
    reviewRequest,
  ]);

  const handleRefresh = () => {
    fetchData();
  };

  const setPreset = (label) => {
    let range = null;
    if (label === 'Today') range = lastNDays(1);
    if (label === '7d') range = lastNDays(7);
    if (label === '30d') range = lastNDays(30);
    if (label === '90d') range = lastNDays(90);
    if (label === '180d') range = lastNDays(180);
    if (label === 'year') range = lastNDays(365);
    if (!range) return;
    setFromDate(range.from);
    setToDate(range.to);
  };

  const baseColumns = useMemo(() => getColumnsForTab(requestType), [requestType]);
  const columns = useMemo(
    () =>
      isPreApprovalQueueView
        ? baseColumns.filter((column) => String(column?.label || '').toLowerCase() !== 'reference')
        : baseColumns,
    [baseColumns, isPreApprovalQueueView]
  );

  // Narrow to revision-targeting-creator rows when the Revisions tab is
  // active. Same source list as my_requests, just filtered client-side so
  // we don't need a new backend view_mode.
  const baseRows = useMemo(() => (
    isRevisionsView
      ? (Array.isArray(requests) ? requests : []).filter(isRevisionTargetingCreator)
      : requests
  ), [isRevisionsView, requests, isRevisionTargetingCreator]);

  const filteredRows = useMemo(() => {
    const raw = (debouncedSearch || '').trim();
    if (!raw) return baseRows;
    const q = raw.toLowerCase();

    // Treat search input as an explicit request-ID lookup when the user
    // types either "#123" or a bare numeric string. Substring matching on
    // free text was matching "388" against unrelated values like "184" in
    // a reference column, so we scope to req.id first when the intent is
    // clearly an ID lookup.
    const idCandidate = raw.startsWith('#') ? raw.slice(1).trim() : raw;
    if (/^\d+$/.test(idCandidate)) {
      const target = String(idCandidate);
      const exact = baseRows.filter((req) => String(req?.id) === target);
      if (exact.length) return exact;
      // If no exact ID hit, also surface rows where the numeric appears
      // *inside* the formal id (e.g. PAR-…-0388) but require a clear ID
      // boundary so '388' doesn't catch '184' or '12388'.
      const idBoundary = new RegExp(`(^|[^0-9])${target}([^0-9]|$)`);
      return baseRows.filter((req) => idBoundary.test(String(req?.id)));
    }

    return baseRows.filter((req) => {
      const requester = req?.user?.name || '';
      const cols = columns
        .map((c) => {
          try {
            const v = c.accessor(req);
            return typeof v === 'string' ? v : v == null ? '' : String(v);
          } catch {
            return '';
          }
        })
        .join(' ');

      return `${requester} ${cols}`.toLowerCase().includes(q);
    });
  }, [baseRows, columns, debouncedSearch]);

  const resolveActionFieldForRow = useCallback((req) => {
    const type = normalizeRequestTypeToken(req?.request_type || requestType);
    const managerStatus = (req.status || '').toLowerCase();
    const hrStatus = (req.hr_status || '').toLowerCase();
    const financeCoordStatus = (req.finance_coordinator_status || '').toLowerCase();
    const finStatus = (req.finance_status || '').toLowerCase();
    const ceoStatus = (req.ceo_status || '').toLowerCase();
    const preApprovalStatus = String(req?.pre_approval_status || '').toLowerCase();
    const preStep = String(req?.pre_approval_current_step || '').toLowerCase();
    const preHod = String(req?.pre_approval_hod_status || '').toLowerCase();
    const preOps = String(req?.pre_approval_operations_status || '').toLowerCase();
    const preCeo = String(req?.pre_approval_ceo_status || '').toLowerCase();
    const preExec = String(req?.pre_approval_executive_md_status || '').toLowerCase();

    if (mainTab !== 'assigned_requests') return null;

    if (hasWorkflowEngineInstance(req)) {
      return getPendingWorkflowTask(req) ? 'workflow_task' : null;
    }

    if (type === 'pre_approval' || isPreApprovalRequest(req)) {
      if (preApprovalStatus === 'revision_requested') {
        return isPreApprovalRevisionAssignee(req, role, myEmployeeCode) ? 'revision_reject' : null;
      }
      if (role === 'hr_admin' && preStep === 'hr_admin' && hrStatus === 'pending') {
        return 'hr_status';
      }
      if (isHodRole(role) && preStep === 'hod' && preHod === 'pending') {
        return 'pre_approval_hod_status';
      }
      if (isOperationsRole(role) && preStep === 'operations_manager' && preOps === 'pending') {
        return 'pre_approval_operations_status';
      }
      if (role === 'ceo' && preStep === 'ceo' && preCeo === 'pending') {
        return 'pre_approval_ceo_status';
      }
      if (isExecutiveRole(role) && preStep === 'executive_managing_director' && preExec === 'pending') {
        return 'pre_approval_executive_md_status';
      }
      return null;
    }

    if (role === 'manager' && managerStatus === 'pending') return 'status';

    if (role === 'hr_admin') {
      const isDirectManager = String(req?.user?.manager_id || '') === String(myEmployeeCode || '');
      if (isDirectManager && managerStatus === 'pending') return 'status';
      if (hrStatus === 'pending') return 'hr_status';
    }

    if (role === 'finance_coordinator') {
      if (type === 'finance_claim' && financeCoordStatus === 'pending') return 'finance_coordinator_status';
    }

    if (role === 'finance') {
      if (
        type === 'finance_claim' &&
        hrStatus === 'approved' &&
        financeCoordStatus === 'approved' &&
        finStatus === 'pending'
      ) {
        return 'finance_status';
      }
      if (type === 'loans' && hrStatus === 'approved' && finStatus === 'pending') {
        return 'finance_status';
      }
    }

    if (role === 'ceo' && type === 'loans' && finStatus === 'approved' && ceoStatus === 'pending') {
      return 'ceo_status';
    }

    return null;
  }, [mainTab, myEmployeeCode, requestType, role]);

  const renderStatusChip = (value) => {
    const normalized = String(value || 'pending').toLowerCase();
    return <StatusBadge status={normalized === 'revision_requested' ? 'revision' : normalized} size="small" />;
  };
  const actionIconButtonSx = {
    width: 34,
    height: 34,
    borderRadius: '8px',
    border: '1px solid',
    borderColor: 'divider',
    transition: 'all 0.15s ease-in-out',
    '&:hover': { transform: 'scale(1.12)', boxShadow: 1 },
    '& .MuiSvgIcon-root': { fontSize: 20 },
  };

  const renderIconActionButton = ({ label, color = 'primary', icon, onClick }) => (
    <Tooltip key={label} title={label} arrow placement="top">
      <IconButton size="small" color={color} aria-label={label} onClick={onClick} sx={actionIconButtonSx}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  const runStatusChange = async (req, newStatus) => {
    const requestId = req?.id;
    if (!requestId) return;

    let rejectionReason = '';

    setUpdating(true);
    setError(null);
    try {
      const workflowPayload = getWorkflowActionPayload(req);
      await updateRequestStatus(
        requestId,
        newStatus,
        Object.keys(workflowPayload).length ? workflowPayload : rejectionReason
      );
      // Optimistically remove the acted-on row from the assigned/pending queue
      // so it doesn't linger with active Approve/Reject icons while the
      // re-fetch is in flight. fetchData() will reconcile shortly after.
      if (mainTab === 'assigned_requests') {
        setRequests((prev) => prev.filter((r) => r.id !== requestId));
      }
      await fetchData();
      if (mainTab !== 'my_requests') await refreshBreakdowns();
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to update request status.');
    } finally {
      setUpdating(false);
      setSelectedIds((arr) => arr.filter((id) => id !== requestId));
    }
  };

  const handleStatusChange = async (e, req, newStatus) => {
    e?.stopPropagation?.();
    const requestId = req?.id;
    if (!requestId) return;

    if (isPreApprovalRequest(req) || isMissingPunchRequest(req)) {
      openPreApprovalReviewDialog(req, newStatus);
      return;
    }

    // Always confirm before acting on a simple assigned-queue request so a
    // misclick on the Approve / Reject icon can't fire a PATCH instantly.
    setConfirmDialog({ open: true, request: req, action: newStatus });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, request: null, action: null });
  };

  const submitConfirmDialog = async () => {
    const { request, action } = confirmDialog;
    closeConfirmDialog();
    if (request && action) {
      await runStatusChange(request, action);
    }
  };

  const renderApproveRejectButtons = (req) => {
    if (mainTab !== 'assigned_requests') return null;

    const field = resolveActionFieldForRow(req);
    if (!field) return null;

    // While a revision is open the assignee can only reject (with a reason);
    // moving the request forward again goes through Edit & Resubmit instead.
    if (field === 'revision_reject') {
      return renderIconActionButton({
        label: 'Reject',
        color: 'error',
        icon: <CancelIcon />,
        onClick: (e) => handleStatusChange(e, req, 'rejected'),
      });
    }

    const showRevise = isPreApprovalRequest(req);

    return (
      <>
        {renderIconActionButton({
          label: 'Approve',
          color: 'success',
          icon: <CheckCircleIcon />,
          onClick: (e) => handleStatusChange(e, req, 'approved'),
        })}
        {showRevise && renderIconActionButton({
          label: 'Revise',
          color: 'primary',
          icon: <EditNoteIcon />,
          onClick: (e) => handleStatusChange(e, req, 'revised'),
        })}
        {renderIconActionButton({
          label: 'Reject',
          color: 'error',
          icon: <CancelIcon />,
          onClick: (e) => handleStatusChange(e, req, 'rejected'),
        })}
      </>
    );
  };

  const renderDesktopRowActions = (req) => (
    <Stack
      direction="row"
      spacing={0.6}
      justifyContent="flex-end"
      alignItems="center"
    >
      {renderIconActionButton({
        label: 'View',
        icon: <VisibilityIcon />,
        onClick: (event) => {
          event.stopPropagation();
          openRequestDetails(req.id);
        },
      })}
      {renderApproveRejectButtons(req)}
    </Stack>
  );

  const bulkUpdate = async (newStatus) => {
    if (!selectedIds.length) return;

    setUpdating(true);
    setError(null);
    try {
      const map = new Map(requests.map((r) => [r.id, r]));
      const toProcess = selectedIds
        .map((id) => ({ id, field: resolveActionFieldForRow(map.get(id)), row: map.get(id) }))
        .filter((x) => !!x.field);

      if (
        toProcess.some(
          (item) =>
            isPreApprovalRequest(item?.row) ||
            isMissingPunchRequest(item?.row) ||
            hasWorkflowEngineInstance(item?.row)
        )
      ) {
        setError(
          'Bulk update is not available for pre-approval, missing punch, or dynamic workflow requests because they require per-request inputs.'
        );
        setUpdating(false);
        return;
      }

      let rejectionReason = '';

      for (const item of toProcess) {
        await updateRequestStatus(item.id, newStatus, rejectionReason);
      }

      await fetchData();
      if (mainTab !== 'my_requests') await refreshBreakdowns();
      if (typeof window.refreshAssignedPendingCount === 'function') {
        window.refreshAssignedPendingCount();
      }
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Bulk update failed for one or more requests.');
    } finally {
      setUpdating(false);
    }
  };

  const exportCsv = async () => {
    if (canExportAllRequestsExcel) {
      setExporting(true);
      setError(null);

      try {
        const exportQueue =
          mainTab === 'assigned_requests'
            ? 'pending'
            : mainTab === 'archive_requests'
              ? 'archive'
              : 'all';
        const response = await exportAllRequestsExcel({
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          request_type: requestType && requestType !== 'all' ? requestType : 'all',
          queue: exportQueue,
          search: debouncedSearch || undefined,
        });
        const filename = filenameFromDisposition(
          response.headers?.['content-disposition'],
          `all_requests_${formatDate(new Date().toISOString())}.xlsx`
        );

        downloadBlob(response.data, filename);
      } catch (err) {
        console.error('Failed to export all requests:', err);
        setError(err?.response?.data?.message || 'Failed to export all requests. Please try again.');
      } finally {
        setExporting(false);
      }
      return;
    }

    const header = ['ID', 'Requested By', ...columns.map((c) => c.label)];
    const rows = filteredRows.map((req) => [
      req.id,
      normalizeDisplayValue(req?.user?.name),
      ...columns.map((c) => {
        try {
          const value = c.accessor(req);
          return String(value ?? '')
            .replace(/\n/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        } catch {
          return '';
        }
      }),
    ]);

    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `hr_requests_${requestType}_${formatDate(new Date().toISOString())}.csv`);
  };

  const actionableRowIds = useMemo(
    () => filteredRows.filter((req) => Boolean(resolveActionFieldForRow(req))).map((req) => req.id),
    [filteredRows, resolveActionFieldForRow]
  );
  const actionableRowIdSet = useMemo(() => new Set(actionableRowIds), [actionableRowIds]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = prev.filter((id) => actionableRowIdSet.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [actionableRowIdSet]);

  const allIds = actionableRowIds;
  const allSelected = selectedIds.length > 0 && selectedIds.length === allIds.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < allIds.length;

  const toggleAll = (checked) => {
    setSelectedIds(checked ? allIds : []);
  };

  const toggleOne = (id, checked) => {
    if (!actionableRowIdSet.has(id)) return;
    setSelectedIds((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((existingId) => existingId !== id)
    );
  };

  const openCreateRequest = useCallback(() => {
    const path =
      requestType && requestType !== 'all'
        ? `/hr-requests/new?type=${requestType}`
        : '/hr-requests/new';
    navigate(path);
  }, [navigate, requestType]);

  const resetFilters = useCallback(() => {
    setSearch('');
    const range = lastNDays(90); // reset to default: last 3 months
    setFromDate(range.from);
    setToDate(range.to);
    setRequestType('all');
    setSelectedIds([]);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const roleLabel = formatWorkflowStepLabel(role || 'employee');
  const pageTitle = isArchiveView
    ? `${roleLabel} Request Archive`
    : isAssignedView
      ? `${roleLabel} Action Queue`
      : isRevisionsView
        ? 'Pre-Approval Revisions'
        : 'My Requests';
  const pageOverline = isArchiveView
    ? 'Reviewed requests'
    : isAssignedView
      ? 'Role queue'
      : isRevisionsView
        ? 'Pending revision'
        : 'Requests';
  const queueSubtitle = loading
    ? 'Loading the latest queue…'
    : isAssignedView
      ? assignedTotal > 0
        ? `${assignedTotal} pending request(s) awaiting your action.`
        : 'Assigned queue is clear. No pending requests need action.'
      : isArchiveView
        ? `${archivedTotal} request(s) you already acted on in your visible scope.`
        : isRevisionsView
          ? revisionsTotal > 0
            ? `${revisionsTotal} pre-approval request(s) returned to you for revision. Click a row to edit and resubmit.`
            : 'No pre-approval revisions are waiting on you right now.'
          : 'Create and track HR requests with a cleaner, faster queue view.';

  const tableTitle = isArchiveView
    ? 'Reviewed archive'
    : isAssignedView
      ? 'Assigned review queue'
      : 'My request activity';

  const tableSubtitle = loading
    ? 'Loading request workspace...'
    : isArchiveView
      ? `${filteredRows.length} reviewed request(s) in the visible archive scope.`
      : isAssignedView
        ? `${filteredRows.length} request(s) currently routed to you for action.`
        : `${filteredRows.length} request(s) created by you in the selected range.`;
  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline={pageOverline}
        title={pageTitle}
        subtitle={queueSubtitle}
        actions={[
          mainTab === 'my_requests' ? (
            <Button key="new" variant="contained" onClick={openCreateRequest}>
              + New Request
            </Button>
          ) : null,
        ].filter(Boolean)}
      />

      <RequestQueueFocusPanel
        roleLabel={roleLabel}
        isAssignedView={isAssignedView}
        isArchiveView={isArchiveView}
        requestType={requestType}
        visibleCount={filteredRows.length}
        assignedTotal={assignedTotal}
        archivedTotal={archivedTotal}
        actionableCount={actionableRowIds.length}
        selectedCount={selectedIds.length}
        loading={loading}
      />

      <RequestsFiltersPanel
        mainTab={mainTab}
        mainTabs={mainTabs}
        onMainTabChange={handleMainTabChange}
        requestType={requestType}
        onRequestTypeChange={handleRequestTypeChange}
        finalSubTabs={finalSubTabs}
        activeBreakdown={activeBreakdown}
        isArchiveView={isArchiveView}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        searchRef={searchRef}
        fromDate={fromDate}
        onFromDateChange={(e) => setFromDate(e.target.value)}
        toDate={toDate}
        onToDateChange={(e) => setToDate(e.target.value)}
        onApply={fetchData}
        onPreset={setPreset}
        onReset={resetFilters}
        onRefresh={handleRefresh}
        onExport={exportCsv}
        exportLabel={canExportAllRequestsExcel ? 'Export Excel' : 'Export'}
        exporting={exporting}
        loading={loading}
        updating={updating}
        canExport={canExportAllRequestsExcel || !!filteredRows.length}
        showBulkActions={isAssignedView && selectedIds.length > 0}
        selectedCount={selectedIds.length}
        onBulkApprove={() => bulkUpdate('approved')}
        onBulkReject={() => bulkUpdate('rejected')}
        showTypeCounts={mainTab !== 'my_requests'}
        showBreakdown={mainTab !== 'my_requests'}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 1.25, borderRadius: '8px' }}>
          {error}
        </Alert>
      ) : null}

      <DataTableShell
        tone="dashboard"
        title={tableTitle}
        subtitle={tableSubtitle}
        loading={loading}
        empty={
          !loading && !filteredRows.length
            ? mainTab === 'my_requests'
              ? null
              : 'No requests found'
            : null
        }
        mobileCards={
          <RequestsMobileCards
            loading={loading}
            rows={filteredRows}
            columns={columns}
            showActions={isAssignedView}
            onRowClick={(row) => openRequestDetails(row.id)}
            renderRowActions={renderApproveRejectButtons}
            renderStatus={renderStatusChip}
            onEmptyAction={mainTab === 'my_requests' ? openCreateRequest : null}
            emptyActionLabel="Create a new request"
          />
        }
        sx={{ borderRadius: '8px', '& .MuiCardContent-root': { p: 0 } }}
      >
        {mdUp ? (
          <RequestsDesktopTable
            loading={loading}
            rows={filteredRows}
            columns={columns}
            selectable={isAssignedView}
            showActionsColumn={isAssignedView}
            selectedIds={selectedIds}
            allSelected={allSelected}
            someSelected={someSelected}
            onToggleAll={toggleAll}
            onToggleOne={toggleOne}
            isRowSelectable={(row) => actionableRowIdSet.has(row.id)}
            onRowClick={(row) => openRequestDetails(row.id)}
            renderRowActions={renderDesktopRowActions}
            renderStatus={renderStatusChip}
            compactActionsColumn={isPreApprovalQueueView}
            onEmptyAction={mainTab === 'my_requests' ? openCreateRequest : null}
            emptyActionLabel="Create a new request"
          />
        ) : null}
      </DataTableShell>

      <Box
        sx={{
          mt: 1.6,
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          opacity: 0.72,
          fontSize: 12.5,
          color: 'text.secondary',
        }}
      >
        <SearchIcon fontSize="small" />
        <Typography variant="caption" color="inherit">
          Tip: Press <kbd>/</kbd> to focus search.
        </Typography>
      </Box>

      <Dialog
        open={reviewDialog.open}
        onClose={closeReviewDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: '8px' } }}
      >
        <DialogTitle>
          {reviewDialog.action === 'approved'
            ? `Approve ${reviewIsMissingPunch ? 'Missing Punch' : 'Pre-Approval'} Request`
            : reviewDialog.action === 'revised'
              ? 'Revise Pre-Approval Request'
              : `Reject ${reviewIsMissingPunch ? 'Missing Punch' : 'Pre-Approval'} Request`}
        </DialogTitle>
        <DialogContent dividers>
          {reviewRequest ? (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ borderRadius: '8px' }}>
                {reviewIsMissingPunch ? (
                  <>
                    Review the submitted punch. If HR changes the time or project before approval,
                    an HR note is required.
                  </>
                ) : (
                  <>
                    Request #{reviewRequest.id} is currently at the{' '}
                    <strong>{formatWorkflowStepLabel(reviewCurrentStep)}</strong> step.
                  </>
                )}
              </Alert>

              {reviewIsMissingPunch ? (
                <Box
                  sx={(muiTheme) => ({
                    p: 2,
                    borderRadius: '8px',
                    border: `1px solid ${alpha(muiTheme.palette.divider, 0.9)}`,
                    bgcolor: alpha(muiTheme.palette.primary.main, 0.04),
                  })}
                >
                  <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                    Submitted punch
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    <Chip label={`Date: ${normalizeDisplayValue(reviewRequest.missing_punch_date)}`} />
                    <Chip label={`Time: ${normalizeDisplayValue(reviewRequest.missing_punch_time)}`} />
                    <Chip label={`Type: ${normalizeDisplayValue(reviewRequest.missing_punch_type)}`} />
                    <Chip
                      label={`Project: ${
                        getProjectLabel(submittedMissingPunchProject) ||
                        normalizeDisplayValue(reviewRequest.missing_punch_project_id)
                      }`}
                    />
                  </Stack>
                  {reviewRequest.missing_punch_reason ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                      Reason: {reviewRequest.missing_punch_reason}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              {reviewIsMissingPunch && reviewDialog.action === 'approved' ? (
                <>
                  <TextField
                    label="Effective Punch Time"
                    type="time"
                    value={reviewDialog.missing_punch_effective_time}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        missing_punch_effective_time: event.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    required
                    helperText="Defaults to the employee submitted time. Change it only if HR is correcting the punch."
                  />

                  <Autocomplete
                    fullWidth
                    options={missingPunchProjectOptions}
                    value={selectedMissingPunchProject}
                    loading={missingPunchProjectsLoading}
                    getOptionLabel={(option) => getProjectLabel(option)}
                    isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
                    onChange={(_, option) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        missing_punch_effective_project_id: option?.id ? String(option.id) : '',
                      }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Effective Project"
                        helperText="Defaults to the submitted project. Change it only if HR is correcting the project."
                      />
                    )}
                  />
                </>
              ) : null}

              {reviewIsMissingPunch ? (
                <TextField
                  label="HR Note"
                  value={reviewDialog.missing_punch_hr_note}
                  onChange={(event) =>
                    setReviewDialog((prev) => ({ ...prev, missing_punch_hr_note: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  required={missingPunchHrNoteRequired}
                  helperText={
                    missingPunchHrNoteRequired
                      ? 'Required because the approved time or project differs from the submitted values.'
                      : reviewDialog.action === 'rejected'
                        ? 'Optional internal HR note. Rejection reason is still required below.'
                        : 'Optional if approving exactly as submitted.'
                  }
                />
              ) : null}

              {reviewIsHodApproval ? (
                <>
                  <TextField
                    label="Total Cost to Company"
                    value={normalizeDisplayValue(reviewRequest?.total_cost_to_company)}
                    InputProps={{ readOnly: true }}
                  />

                  <FormControl fullWidth required>
                    <InputLabel id="pre-approval-billing-mode-label">Billing Mode</InputLabel>
                    <Select
                      labelId="pre-approval-billing-mode-label"
                      label="Billing Mode"
                      value={reviewDialog.pre_approval_billing_mode}
                      onChange={(event) =>
                        setReviewDialog((prev) => ({
                          ...prev,
                          pre_approval_billing_mode: event.target.value,
                          marginal_benefit:
                            event.target.value === 'billed' ? String(reviewComputedMargin || '') : prev.marginal_benefit,
                        }))
                      }
                    >
                      {PRE_APPROVAL_BILLING_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>HOD must complete commercial inputs before approval.</FormHelperText>
                  </FormControl>

                  <TextField
                    label={
                      reviewBillingMode === 'lumpsum'
                        ? 'Lumpsum Amount'
                        : 'Client Billing (No VAT)'
                    }
                    type={reviewIsBillingHq ? 'text' : 'number'}
                    value={reviewIsBillingHq ? 'N/A' : reviewDialog.client_billing}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({ ...prev, client_billing: event.target.value }))
                    }
                    inputProps={reviewIsBillingHq ? undefined : { min: 0, step: '0.01' }}
                    required={!reviewIsBillingHq}
                    InputProps={{ readOnly: reviewIsBillingHq }}
                    helperText={reviewIsBillingHq ? 'Not applicable for Billing HQ.' : undefined}
                  />

                  <TextField
                    label="Margin Benefit %"
                    type={reviewIsBillingHq ? 'text' : 'number'}
                    value={
                      reviewIsBillingHq
                        ? 'N/A'
                        : reviewBillingMode === 'billed'
                        ? reviewComputedMargin
                        : reviewDialog.marginal_benefit
                    }
                    onChange={(event) =>
                      setReviewDialog((prev) => ({ ...prev, marginal_benefit: event.target.value }))
                    }
                    inputProps={reviewIsBillingHq ? undefined : { min: 0, step: '0.01' }}
                    required={reviewBillingMode === 'lumpsum'}
                    disabled={reviewIsBillingHq || reviewBillingMode === 'billed'}
                    helperText={
                      reviewIsBillingHq
                        ? 'Not applicable for Billing HQ.'
                        : reviewBillingMode === 'billed'
                        ? 'Auto-calculated from total cost to company and client billing.'
                        : 'Required when billing mode is lumpsum.'
                    }
                  />
                </>
              ) : null}

              {reviewDialog.action === 'revised' ? (
                <FormControl fullWidth required>
                  <InputLabel id="pre-approval-revision-target-label">Revision Target</InputLabel>
                  <Select
                    labelId="pre-approval-revision-target-label"
                    label="Revision Target"
                    value={reviewDialog.revision_target_step}
                    onChange={(event) =>
                      setReviewDialog((prev) => ({
                        ...prev,
                        revision_target_step: event.target.value,
                      }))
                    }
                  >
                    {reviewTargetOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    Revision can only go back to the creator or, after HOD, back to HOD.
                  </FormHelperText>
                </FormControl>
              ) : null}

              {reviewDialog.action !== 'approved' ? (
                <TextField
                  label={reviewDialog.action === 'revised' ? 'Revision Reason' : 'Rejection Reason'}
                  value={reviewDialog.reason}
                  onChange={(event) =>
                    setReviewDialog((prev) => ({ ...prev, reason: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  required
                />
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReviewDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitPreApprovalReview} disabled={updating}>
            {updating
              ? 'Saving...'
              : reviewDialog.action === 'approved'
                ? 'Approve'
                : reviewDialog.action === 'revised'
                  ? 'Send Revision'
                  : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        PaperProps={{ sx: { borderRadius: '8px' } }}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to{' '}
            <strong>{confirmDialog.action === 'approved' ? 'approve' : 'reject'}</strong> this request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} disabled={updating}>Cancel</Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'approved' ? 'success' : 'error'}
            onClick={submitConfirmDialog}
            disabled={updating}
          >
            {updating
              ? 'Saving...'
              : confirmDialog.action === 'approved'
                ? 'Yes, Approve'
                : 'Yes, Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}

export default RequestsWithAssignedSplit;
